import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Paper,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Divider,
  Avatar,
  Grid,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import config from "../../config";

export default function AdminDetail() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchAdminDetail();
  }, [id]);

  const fetchAdminDetail = async () => {
    try {
      setLoading(true);
      setError("");

      const token = sessionStorage.getItem("token");

      const res = await fetch(`${config.apiBaseUrl}/admin/get/${id}`, {
        method: "GET",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (data.success) {
        setAdmin(data.admin);
      } else {
        setAdmin(null);
        setError(data.message || "Admin not found");
      }
    } catch (err) {
      setAdmin(null);
      setError("Something went wrong while loading admin details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "—";
    try {
      return new Date(value).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return value;
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!currentPassword || !newPassword) {
      setSnackbar({
        open: true,
        message: "Please fill in both password fields",
        severity: "error",
      });
      return;
    }

    try {
      setChangePasswordLoading(true);

      const token = sessionStorage.getItem("token");

      const res = await fetch(
        `${config.apiBaseUrl}/admin/auth/change-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            currentPassword,
            newPassword,
          }),
        }
      );

      const data = await res.json();

      if (data.success) {
        setSnackbar({
          open: true,
          message: data.message || "Password changed successfully",
          severity: "success",
        });
        setCurrentPassword("");
        setNewPassword("");
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to change password",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Something went wrong while changing password",
        severity: "error",
      });
    } finally {
      setChangePasswordLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: "#F8FAFC" }}>
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <Box component="main" sx={{ flexGrow: 1 }}>
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            mb={4}
          >
            {isMobile && (
              <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "12px",
                }}
              >
                <MenuRoundedIcon />
              </IconButton>
            )}

            <IconButton
              onClick={() => navigate("/admins")}
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "12px",
              }}
            >
              <ArrowBackRoundedIcon />
            </IconButton>

            <Typography variant="h4" fontWeight={800}>
              Admin Details
            </Typography>
          </Stack>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 10,
              }}
            >
              <Stack alignItems="center" spacing={2}>
                <CircularProgress />
                <Typography>Loading admin details...</Typography>
              </Stack>
            </Box>
          ) : error ? (
            <Paper sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
              <Typography color="error" fontWeight={600} mb={2}>
                {error}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate("/admins")}
                sx={{ textTransform: "none", borderRadius: "12px" }}
              >
                Back to Admin Management
              </Button>
            </Paper>
          ) : admin ? (
            <Grid container spacing={3}>
              {/* LEFT COLUMN - Profile & details */}
              <Grid item xs={12} md={6}>
                <Paper
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      p: { xs: 3, md: 4 },
                      background:
                        "linear-gradient(135deg,#0F172A 0%,#111827 50%,#1E293B 100%)",
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                      color: "#fff",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={2.5}
                      alignItems="center"
                    >
                      <Avatar
                        sx={{
                          width: 64,
                          height: 64,
                          background:
                            "linear-gradient(135deg,#3B82F6 0%,#06B6D4 100%)",
                          fontSize: "1.5rem",
                          fontWeight: 700,
                        }}
                      >
                        {admin.name ? admin.name.charAt(0).toUpperCase() : "A"}
                      </Avatar>

                      <Box>
                        <Typography variant="h5" fontWeight={800}>
                          {admin.name}
                        </Typography>
                        <Chip
                          label={admin.accessLevel}
                          size="small"
                          sx={{
                            mt: 0.5,
                            background: "rgba(255,255,255,0.12)",
                            color: "#fff",
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Stack>
                  </Box>

                  {/* Details list */}
                  <Box sx={{ p: { xs: 3, md: 4 } }}>
                    <Stack spacing={2.5} divider={<Divider flexItem />}>
                      <DetailRow
                        icon={<BadgeOutlinedIcon />}
                        label="Admin ID"
                        value={admin.adminId}
                      />

                      <DetailRow
                        icon={<EmailOutlinedIcon />}
                        label="Email"
                        value={admin.email}
                      />

                      <DetailRow
                        icon={<ShieldOutlinedIcon />}
                        label="Access Level"
                        value={admin.accessLevel}
                      />

                      {admin.createdBy && (
                        <DetailRow
                          icon={<PersonOutlineRoundedIcon />}
                          label="Created By"
                          value={admin.createdBy}
                        />
                      )}

                      <DetailRow
                        icon={<CalendarTodayOutlinedIcon />}
                        label="Created At"
                        value={formatDate(admin.createdAt)}
                      />

                      <DetailRow
                        icon={<UpdateOutlinedIcon />}
                        label="Last Updated"
                        value={formatDate(admin.updatedAt)}
                      />
                    </Stack>
                  </Box>
                </Paper>
              </Grid>

              {/* RIGHT COLUMN - Change password */}
              <Grid item xs={12} md={6}>
                <Paper
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                    p: { xs: 3, md: 4 },
                    height: "100%",
                  }}
                >
                  <Typography variant="h6" fontWeight={800} mb={0.5}>
                    Change Password
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#6B7280", mb: 3 }}
                  >
                    Update the password for this admin account
                  </Typography>

                  <form onSubmit={handleChangePassword}>
                    <TextField
                      fullWidth
                      label="Current Password"
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      margin="normal"
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlinedIcon sx={{ color: "#6B7280" }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowCurrentPassword(!showCurrentPassword)
                              }
                              edge="end"
                            >
                              {showCurrentPassword ? (
                                <VisibilityOff sx={{ color: "#6B7280" }} />
                              ) : (
                                <Visibility sx={{ color: "#6B7280" }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 2,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "14px",
                          background: "#F9FAFB",
                        },
                      }}
                    />

                    <TextField
                      fullWidth
                      label="New Password"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      margin="normal"
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlinedIcon sx={{ color: "#6B7280" }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                              edge="end"
                            >
                              {showNewPassword ? (
                                <VisibilityOff sx={{ color: "#6B7280" }} />
                              ) : (
                                <Visibility sx={{ color: "#6B7280" }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 3,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "14px",
                          background: "#F9FAFB",
                        },
                      }}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={changePasswordLoading}
                      sx={{
                        height: 52,
                        borderRadius: "14px",
                        textTransform: "none",
                        fontSize: "1rem",
                        fontWeight: 700,
                        background:
                          "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                        boxShadow: "0 12px 30px rgba(37,99,235,0.25)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)",
                        },
                      }}
                    >
                      {changePasswordLoading ? (
                        <CircularProgress size={22} sx={{ color: "#fff" }} />
                      ) : (
                        "Update Password"
                      )}
                    </Button>
                  </form>
                </Paper>
              </Grid>
            </Grid>
          ) : null}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar((prev) => ({ ...prev, open: false }))
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function DetailRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "12px",
          background: "#F1F5F9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#2563EB",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box>
        <Typography variant="body2" sx={{ color: "#6B7280" }}>
          {label}
        </Typography>
        <Typography fontWeight={600} sx={{ color: "#111827" }}>
          {value || "—"}
        </Typography>
      </Box>
    </Stack>
  );
}